import React, { useMemo } from "react";
import { useFormikContext } from "formik";
import { mapValues } from "@common/helpers/objects";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import { WORKSPACE_ROLES_LABELS } from "@common/constants";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import {
    Section,
    SectionedCheckboxTable,
} from "@design-system/SectionedCheckboxTable/SectionedCheckboxTable";
import { Picker } from "@design-system/Picker/Picker.lib";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { Alert } from "@design-system/Alert/Alert";
import { Field } from "@design-system/Field/Field";
import { shouldShowFieldError } from "@helpers/form";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import styles from "./MemberManagement.module.scss";
import { useAssignableWorkspaceLevelRoleOptions } from "./useAssignableWorkspaceLevelRoleOptions";
import { AssignableWorkspaceRole, workspaceLevelRoleDescription } from "./lib";
import { MemberPermissionsSchema, TeamFormProps } from "./types";

const fields = createFormikFields<MemberPermissionsSchema>();

const isAssignableWorkspaceRole = (
    role: UserWorkspaceRole,
): role is AssignableWorkspaceRole =>
    Object.keys(workspaceLevelRoleDescription).some(
        (assignableRole) => assignableRole === role,
    );

export const ConfigurePermissionsFields: React.FC<TeamFormProps> = ({
    actingUserRole,
    entities,
}) => {
    const workspaceLevelRoleOptions =
        useAssignableWorkspaceLevelRoleOptions(actingUserRole);

    const formikContext = useFormikContext<MemberPermissionsSchema>();
    const workspaceRole = formikContext.values.workspaceRole;
    const selectedWorkspaceRole = isAssignableWorkspaceRole(workspaceRole)
        ? workspaceRole
        : null;
    const roleMeta = formikContext.getFieldMeta("workspaceRole");
    const isRoleInvalid = shouldShowFieldError({
        error: roleMeta.error,
        touched: roleMeta.touched,
        submitCount: formikContext.submitCount,
    });

    const roleItems = useMemo(
        () =>
            workspaceLevelRoleOptions.map((role) => ({
                value: role,
                label: WORKSPACE_ROLES_LABELS[role],
                description: workspaceLevelRoleDescription[role],
            })),
        [workspaceLevelRoleOptions],
    );

    const preventSelectingIndividualEntities =
        selectedWorkspaceRole === UserWorkspaceRole.ADMIN;

    const businessEntities = useMemo(
        () => entities.filter((e) => e.isBusiness),
        [entities],
    );
    const personalEntities = useMemo(
        () => entities.filter((e) => !e.isBusiness),
        [entities],
    );

    const handleWorkspaceRoleChange = async (role: AssignableWorkspaceRole) => {
        await formikContext.setFieldValue("workspaceRole", role);
        await formikContext.setFieldValue(
            "entityAssignments",
            mapValues(
                formikContext.values.entityAssignments,
                (selected) => role === UserWorkspaceRole.ADMIN || selected,
            ),
        );
        await formikContext.setFieldValue(
            "assignToNewEntities",
            role === UserWorkspaceRole.ADMIN ||
                formikContext.values.assignToNewEntities,
        );
    };

    const sections: Array<Section<string>> = useMemo(() => {
        const result: Array<Section<string>> = [];

        if (businessEntities.length > 0) {
            result.push({
                title: "Business",
                items: businessEntities.map((entity) => ({
                    value: entity.id.toString(),
                    disabled: preventSelectingIndividualEntities,
                    render: () => (
                        <>
                            <EntityAvatar
                                entity={entity}
                                size="xs"
                                aria-hidden
                            />
                            {entity.name}
                        </>
                    ),
                    searchValue: entity.name ?? undefined,
                })),
                allowSelectAll: true,
            });
        }

        if (personalEntities.length > 0) {
            const personalItems = personalEntities.map((entity) => ({
                value: entity.id.toString(),
                disabled: preventSelectingIndividualEntities,
                render: () => (
                    <>
                        <EntityAvatar entity={entity} size="xs" aria-hidden />
                        {entity.name}
                    </>
                ),
                searchValue: entity.name ?? undefined,
            }));

            result.push({
                title: "Personal",
                items: personalItems,
                allowSelectAll: true,
            });
        }

        return result;
    }, [
        businessEntities,
        personalEntities,
        preventSelectingIndividualEntities,
    ]);

    const selectedValues = useMemo(() => {
        const result: string[] = [];

        // Add selected entity IDs
        Object.entries(formikContext.values.entityAssignments).forEach(
            ([entityId, isSelected]) => {
                if (isSelected) {
                    result.push(entityId);
                }
            },
        );

        // Add assignToNewEntities if selected
        if (formikContext.values.assignToNewEntities) {
            result.push("assignToNewEntities");
        }

        return result;
    }, [
        formikContext.values.entityAssignments,
        formikContext.values.assignToNewEntities,
    ]);

    const handleSelectionChange = async (values: string[]) => {
        const newEntityAssignments: Record<string, boolean> = {};
        let newAssignToNewEntities = false;

        // Process selected values
        values.forEach((value) => {
            if (value === "assignToNewEntities") {
                newAssignToNewEntities = true;
            } else {
                newEntityAssignments[value] = true;
            }
        });

        // Ensure all entities are represented in entityAssignments
        entities.forEach((entity) => {
            if (!(entity.id.toString() in newEntityAssignments)) {
                newEntityAssignments[entity.id.toString()] = false;
            }
        });

        await formikContext.setFieldValue(
            "entityAssignments",
            newEntityAssignments,
        );
        await formikContext.setFieldValue(
            "assignToNewEntities",
            newAssignToNewEntities,
        );
    };

    return (
        <>
            <section className={styles.roleSection}>
                <header className={styles.sectionHeader}>
                    <h6 className={styles.sectionTitle}>Assign role</h6>
                </header>

                <Field.Root
                    className={styles.roleSelectGroup}
                    invalid={isRoleInvalid}
                >
                    <Picker.Select
                        items={roleItems}
                        value={selectedWorkspaceRole}
                        onValueChange={(role) => {
                            if (role) {
                                handleWorkspaceRoleChange(role);
                            }
                        }}
                        placeholder={
                            WORKSPACE_ROLES_LABELS[workspaceRole] ??
                            "Select role"
                        }
                        aria-invalid={isRoleInvalid}
                        trigger={<Picker.Button variant="primary" size="xl" />}
                    />
                </Field.Root>
            </section>

            {preventSelectingIndividualEntities ? (
                <Alert variant="default" header="All entities included">
                    Admins have full access to all current and future entities
                    in this workspace. Individual entity selection is not
                    required.
                </Alert>
            ) : (
                <section className={styles.section}>
                    <header className={styles.sectionHeader}>
                        <h6 className={styles.sectionTitle}>Entities</h6>
                    </header>
                    <SectionedCheckboxTable
                        sections={sections}
                        value={selectedValues}
                        onChange={handleSelectionChange}
                        searchable
                        disabled={preventSelectingIndividualEntities}
                        className={styles.entitySelectorContainer}
                    />
                    <div
                        className={styles.assignToNewEntitiesContainer}
                        onClick={(event) => {
                            // The checkbox toggles itself; only handle clicks elsewhere on the row.
                            if (
                                preventSelectingIndividualEntities ||
                                (event.target instanceof Element &&
                                    event.target.closest("[role='checkbox']"))
                            ) {
                                return;
                            }
                            void formikContext.setFieldValue(
                                "assignToNewEntities",
                                !formikContext.values.assignToNewEntities,
                            );
                        }}
                    >
                        <div>New business entities</div>
                        <fields.Checkbox
                            name="assignToNewEntities"
                            label="Include new business entities"
                            labelHidden
                            className={styles.assignToNewEntitiesField}
                            disabled={preventSelectingIndividualEntities}
                        />
                    </div>
                </section>
            )}
        </>
    );
};
